import { useState } from "react";
import BusinessDetailsModel from "./formStepComponents/BusinessDetails";
import BussinessAddressModel from "./formStepComponents/BussinessAddress";
import DescribeBusinessModel from "./formStepComponents/DescribeBusiness";
import EPLIModel from "./formStepComponents/EPLI";
import BusinessAttributesModel from "./formStepComponents/BusinessAttributes";
import EmployeeAttributesModel from "./formStepComponents/EmployeeAttributes";
import DocumentsModel from "./formStepComponents/Documents";
import ReviewAndSubmitModel from "./formStepComponents/ReviewAndSubmit";
import styles from "./WorkerCompApplication.module.css";
import Stepper from "uicomponents/Stepper";
import { PageHeader } from "@justworkshr/milo-components";
import {
  PAGE_TITLE0,
  PAGE_TITLE04,
  PAGE_TITLE4,
  PAGE_TITLE_LINK,
} from "./utils/contants";
import { hasQueryParam } from "utils/helper";
import { errorMessageMap, getCWRedirectUrl } from "utils/helper";

import Alert from "uicomponents/Alert";
import {
  trackFormError,
  trackFormSubmission,
  trackSaveAndContinueUserStep,
  trackNewStepCompleted,
  trackStepperError,
} from "utils/analytics/trackEvent";
import { sectionsQuestions } from "./utils/sectionsQuestions";
import OwnerAndEmployeeCensusModel from "./formStepComponents/OwnerAndEmployeeCensus";
import Loader from "uicomponents/Loader";
import Congratulations from "./formStepComponents/Congratulations";

import { StepDataType, postApplicationStepUpdate } from "utils/stepApiHelpers";
import {
  WORKERS_COMP_STEP_LABELS,
  WORKERS_COMP_STEP_LABELS_PREFILL,
  placeholderWCStepData,
} from "utils/constants";
import { useOktaAuth } from "@okta/okta-react";

interface IWorkerCompApplicationProps {
  applicationUuid?: string;
  stepData?: StepDataType | null;
}

const WorkerCompApplicationTestVariant = (
  props: IWorkerCompApplicationProps
): JSX.Element => {
  const stepData = props.stepData?.application_uuid
    ? props.stepData
    : placeholderWCStepData;

  const [errorMessage, setErrorMessage] = useState("");
  const [lastCompletedStep, setLastCompletedStep] = useState(
    stepData?.current_step || 0
  );
  const [activeStepIndex, setActiveStepIndex] = useState(
    stepData?.current_step || 0
  );
  const [stepError, setStepError] = useState(false);
  const { authState } = useOktaAuth();

  const [appComplete, setAppComplete] = useState(false);

  const currentStep = stepData?.steps[activeStepIndex];

  const v2_dedupe_census_flow = hasQueryParam("usecensustab");

  const nextStep = async () => {
    try {
      if (!props.applicationUuid) {
        trackStepperError("uuid not found");
        setStepError(true);
        return;
      }
      if (!stepData) {
        setStepError(true);
        return;
      }

      if (lastCompletedStep === activeStepIndex && !!currentStep) {
        await postApplicationStepUpdate({
          stepName: currentStep.step_name,
          completed: true,
          token: authState?.accessToken?.accessToken || "",
        });

        trackNewStepCompleted(currentStep.step_name);

        setLastCompletedStep(lastCompletedStep + 1);
        setActiveStepIndex(activeStepIndex + 1);

        currentStep?.step_name === "review"
          ? trackFormSubmission(activeStepIndex, "Review")
          : trackSaveAndContinueUserStep(
              activeStepIndex,
              Object.entries(sectionsQuestions)?.[activeStepIndex]?.[0]
            );

        // Redirects to /quoting-checklist dashboard upon submission
        if (activeStepIndex === stepData?.total_steps - 1) {
          setAppComplete(true);
          setTimeout(function () {
            window.location.href = getCWRedirectUrl(
              "/onboarding_checklist?wcappsubmitted=true"
            );
          }, 3000);
        }
      } else {
        setActiveStepIndex(lastCompletedStep || 0);
      }
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, `error updating form step- ${error?.message}`);
      setStepError(true);
      return;
    }
  };

  const previousStep = (): void => {
    setActiveStepIndex(activeStepIndex - 1);
  };

  const formList: { [key: string]: any } = {
    business_details: (
      <BusinessDetailsModel
        setNextStep={nextStep}
        setPreviousStep={previousStep}
      />
    ),
    business_address: (
      <BussinessAddressModel
        setNextStep={nextStep}
        setPreviousStep={previousStep}
      />
    ),
    employee_list: (
      <OwnerAndEmployeeCensusModel
        setNextStep={nextStep}
        setPreviousStep={previousStep}
      />
    ),
    employee_activity_information: (
      <EmployeeAttributesModel
        setNextStep={nextStep}
        setPreviousStep={previousStep}
      />
    ),
    about_business: (
      <DescribeBusinessModel
        setNextStep={nextStep}
        setPreviousStep={previousStep}
      />
    ),
    business_attributes: (
      <BusinessAttributesModel
        setNextStep={nextStep}
        setPreviousStep={previousStep}
      />
    ),
    documents: (
      <DocumentsModel setNextStep={nextStep} setPreviousStep={previousStep} />
    ),
    epli: <EPLIModel setNextStep={nextStep} setPreviousStep={previousStep} />,
    review: (
      <ReviewAndSubmitModel
        setNextStep={nextStep}
        setPreviousStep={previousStep}
        setActiveStepIndex={setActiveStepIndex}
      />
    ),
  };

  let CurrentStepComponent = currentStep ? (
    formList[currentStep.step_name]
  ) : (
    <Loader />
  );

  if (appComplete) {
    CurrentStepComponent = <Loader message="Submitting your application" />;
  }

  const showStepper =
    activeStepIndex <= (stepData?.total_steps || 0) && !appComplete;

  const getPageTitle = () => {
    if (activeStepIndex >= 4) {
      return PAGE_TITLE4;
    }
    if (activeStepIndex >= 0 && activeStepIndex <= 3) {
      return PAGE_TITLE04;
    }
    return PAGE_TITLE0;
  };

  if (stepData?.status === "completed") {
    return <Congratulations />;
  }

  const stepperList = stepData
    ? stepData.steps.map((step) => ({
        label:
          (v2_dedupe_census_flow
            ? WORKERS_COMP_STEP_LABELS_PREFILL[step.step_name]
            : WORKERS_COMP_STEP_LABELS[step.step_name]) || "",
      }))
    : [];

  return (
    <div className={styles.container}>
      {!appComplete && (
        <div className={styles.titleContainer}>
          <PageHeader
            className={styles.pageHeader}
            title={getPageTitle()}
            linkPrevious={
              <a href={getCWRedirectUrl("/onboarding_checklist")}>
                {PAGE_TITLE_LINK}
              </a>
            }
          />
        </div>
      )}
      <div className={styles.formContainer}>
        {showStepper && (
          <Stepper
            steps={stepperList}
            currentStep={activeStepIndex + 1}
            status={lastCompletedStep + 1}
            onClick={(stepNumber) => setActiveStepIndex(stepNumber - 1)}
          />
        )}
        <div
          className={`${
            showStepper ? styles.formDiv : styles.fullWidth
          } ApplicationStep${activeStepIndex}`}
        >
          {CurrentStepComponent}
          {stepError && <Alert message={errorMessage} />}
        </div>
      </div>
    </div>
  );
};

export default WorkerCompApplicationTestVariant;
